import { ComponentType } from '@angular/cdk/overlay';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  signal,
  Signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WidgetChangeDetectionObject } from '@api-response-types/forecast-series.response';
import { HighchartsChartData } from '@api-response-types/highcharts-chart-data.response';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { FilterValue } from '@api-types/types/filter-value.type';
import { Filter } from '@api-types/types/filter.type';
import { Timespan } from '@api-types/types/timespan.type';
import {
  FormFieldComponent,
  FormInputSelectComponent,
  H3Component,
  PmButtonComponent,
  PmTooltipDirective,
} from '@libs/ui';
import { NgxVirtualSelectFieldOptionModel } from '@components/base-elements/virtual-select-field';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartOptionsBuilder } from 'demand-forecasting/components/chart/chart-options-builder';
import { ForecastsHttpClient } from 'demand-forecasting/services/http/forecast-http-client';
import { InsightsHttpClient } from 'demand-forecasting/services/http/insights-http-client';
import { selectAvailableFiltersAsRecord } from 'demand-forecasting/store/filter/filter.selectors';
import { latestCompletedForecastSeries } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import {
  FORECAST_PLOT_BAND,
  FORECAST_PLOT_LINE,
} from 'demand-forecasting/types/forecast-chart/forecast-chart-options';
import { HighchartsOptions } from 'demand-forecasting/types/forecast-chart/highcharts-options';
import Highcharts, { SeriesSplineOptions } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { LucideScan } from '@lucide/angular';
import { DefaultSelectionYearToDateTrendWidget } from '../../types/types';
import { calculatePlotbandStartDateAndSeriesMaxDate } from '../../utils';
import {
  WidgetActionsDirective,
  WidgetBaseComponent,
  WidgetContentDirective,
  WidgetHeaderDirective,
  WidgetTitleDirective,
} from '../widget-base/widget-base.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'pm-year-to-date-trend-widget',
  templateUrl: './year-to-date-trend-widget.component.html',
  imports: [
    MatFormFieldModule,
    TranslateModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    HighchartsChartComponent,
    ReactiveFormsModule,
    FormsModule,
    WidgetBaseComponent,
    WidgetTitleDirective,
    WidgetHeaderDirective,
    WidgetActionsDirective,
    WidgetContentDirective,
    PmTooltipDirective,
    H3Component,
    PmButtonComponent,
    FormFieldComponent,
    FormInputSelectComponent,
  ],
})
export class YearToDateTrendWidgetComponent extends WidgetBaseComponent<YearToDateTrendWidgetComponent> {
  @Input({
    required: false,
  })
  targetUnit?: string;

  @Input({
    required: false,
  })
  filterColumn?: string;

  @Input({
    required: false,
  })
  filterOptions?: FilterValue[];

  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  protected readonly MAX_NUMBER_OF_SELECTIONS = 5;

  protected readonly scanIcon = LucideScan;

  private readonly insightsHttpClient: InsightsHttpClient =
    inject(InsightsHttpClient);
  datasetHasValidDataPoints = true;
  protected chartOptions = signal<HighchartsOptions>(
    new ChartOptionsBuilder().build(),
  );
  chart?: Highcharts.Chart;
  chartUpdated = false;

  selectedValues: FilterValue[] = [];
  selectedValue: FilterValue | null = null;
  possibleFilterValues = signal<FilterValue[]>([]);

  chartData: HighchartsChartData;
  private plotBandStartDate = 0;
  private seriesMaxDate = 0;

  destroyRef = inject(DestroyRef);

  forecastSeries = this.store.selectSignal(latestCompletedForecastSeries);
  private readonly forecastVersion = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );
  filters: Signal<Filter[]> = this.store.selectSignal(
    selectAvailableFiltersAsRecord,
  );

  private timespan: Timespan | undefined;
  filterOptionsForSelect: Signal<NgxVirtualSelectFieldOptionModel<Filter>[]> =
    computed(() =>
      this.filters().map((f) => ({
        label: f.column,
        value: f,
        disabled: false,
      })),
    );

  filterValuesForSelect: Signal<
    NgxVirtualSelectFieldOptionModel<FilterValue>[]
  > = computed(() =>
    this.possibleFilterValues().map((v) => ({
      value: v,
      label: String(v),
      disabled: false,
    })),
  );

  protected selectedFilterFormControl = new FormControl<Filter>(
    {
      value: this.filters()[0],
      disabled: false,
    },
    {
      nonNullable: true,
    },
  );

  protected selectedValuesFormControl = new FormControl<FilterValue[]>(
    {
      value: [],
      disabled: false,
    },
    { nonNullable: true },
  );

  previousTrendAnalysisChangeDetectionObject: WidgetChangeDetectionObject;

  private readonly forecastHttpClient = inject(ForecastsHttpClient);
  public readonly data: DefaultSelectionYearToDateTrendWidget = inject(
    MAT_DIALOG_DATA,
    {
      optional: true,
    },
  );

  constructor() {
    super();
    if (this.data) {
      this.targetUnit = this.data.targetUnit;
      this.filterColumn = this.data.filterColumn;
      this.filterOptions = this.data.filterOptions;
    }

    effect(() => {
      const forecastSeries = this.forecastSeries();
      const forecastVersion = this.forecastVersion();
      const filters = this.filters();
      if (filters && !this.selectedFilterFormControl.getRawValue()) {
        this.selectedFilterFormControl.setValue(filters[0]);
      }

      if (
        !forecastSeries ||
        !forecastVersion ||
        forecastVersion.status !== ForecastVersionStatus.FINISHED ||
        !this.selectedFilterFormControl.getRawValue()
      ) {
        console.warn(
          '[WIDGET_TREND_ANALYSIS] No forecast series or forecast version or selected filters found',
        );
        return;
      }

      const newSeriesChangeDetectionObject: WidgetChangeDetectionObject = {
        _resultSetStatus: forecastSeries._resultSetStatus,
        configurationId: forecastSeries.configurationId,
        resultSetId: forecastVersion.id, // TODO forecastSeries.resultSetId seems to never get updated -> Fix via https://pacemaker-ai.atlassian.net/browse/PM-1674
        yAxis: forecastSeries.yAxis,
      };

      if (
        JSON.stringify(newSeriesChangeDetectionObject) ===
        JSON.stringify(this.previousTrendAnalysisChangeDetectionObject ?? {})
      ) {
        return;
      }
      // Update previous values
      this.previousTrendAnalysisChangeDetectionObject =
        newSeriesChangeDetectionObject;

      this.datasetHasValidDataPoints = this.hasValidDataPoints();
      if (!this.datasetHasValidDataPoints) {
        console.warn('[WIDGET_TREND_ANALYSIS] No valid data points found');
        this.isLoading = false;
      }

      if (this.timespan) {
        const { plotBandStartDate, seriesMaxDate } =
          calculatePlotbandStartDateAndSeriesMaxDate(
            this.timespan,
            Resolution.Monthly,
          );
        this.updatePlotDates(plotBandStartDate, seriesMaxDate);
      } else {
        this.getTimespanAndAdjustPlotDates(forecastVersion.forecastId);
      }

      this.setDefaultFilter();
      this.fetchData();
    });

    this.selectedFilterFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((changedValue) => {
        if (changedValue) this.onFilterChange();
      });
    this.selectedValuesFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onFilterValueChange();
      });
  }

  private getTimespanAndAdjustPlotDates(forecastId: string) {
    this.forecastHttpClient.getTimespan(forecastId).subscribe({
      next: (timespan) => {
        this.timespan = timespan;
        const { plotBandStartDate, seriesMaxDate } =
          calculatePlotbandStartDateAndSeriesMaxDate(
            this.timespan,
            Resolution.Monthly,
          );
        this.updatePlotDates(plotBandStartDate, seriesMaxDate);
      },
      error: (err) => {
        console.error('Error fetching timespan:', err);
      },
    });
  }

  private updatePlotDates(plotbandStartDate: number, seriesMaxDate: number) {
    this.plotBandStartDate = plotbandStartDate;
    this.seriesMaxDate = seriesMaxDate;
  }

  getDefaultSelectionsForFilterValues() {
    const formControlValue = this.selectedFilterFormControl.getRawValue();
    const values = formControlValue?.values;
    if (values && values.length > this.MAX_NUMBER_OF_SELECTIONS) {
      return values.slice(0, this.MAX_NUMBER_OF_SELECTIONS);
    }
    if (values) {
      return values;
    }
    return [];
  }

  hasValidDataPoints(): boolean {
    const forecastSeriesData = this.forecastSeries();
    if (!forecastSeriesData || !forecastSeriesData.originalDates) {
      return false;
    }

    const { initialForecastIndex } = forecastSeriesData;
    const referenceDateMilliseconds =
      forecastSeriesData.originalDates[initialForecastIndex][0];
    const referenceDate = new Date(referenceDateMilliseconds);

    const minus18Months = new Date(
      referenceDate.setMonth(referenceDate.getMonth() - 18),
    );
    const plus6Months = new Date(
      referenceDate.setMonth(referenceDate.getMonth() + 24),
    ); // 24 because we already subtracted 18 months

    const earliestDateMilliseconds = minus18Months.getTime();
    const latestDateMilliseconds = plus6Months.getTime();

    const originalDatesMilliseconds = forecastSeriesData.originalDates.map(
      (date) => date[0],
    );

    const hasDataForRange =
      originalDatesMilliseconds.some(
        (date) => date <= earliestDateMilliseconds,
      ) &&
      originalDatesMilliseconds.some((date) => date >= latestDateMilliseconds);

    if (!hasDataForRange) {
      this.selectedValuesFormControl.disable();
      this.selectedFilterFormControl.disable();
      return false;
    }
    this.selectedValuesFormControl.enable();
    this.selectedFilterFormControl.enable();
    return true;
  }

  async setChartOptions(dataSeries?: SeriesSplineOptions[]) {
    let seriesObject: SeriesSplineOptions = {
      data:
        this.chartData[
          String(this.selectedValuesFormControl.getRawValue()[0])
        ] || [],
      type: 'spline',
      states: {
        hover: {
          lineWidthPlus: 0,
        },
      },
      name: String(this.selectedValuesFormControl.getRawValue()[0]),
    };
    if (dataSeries === undefined) {
      dataSeries = [];

      const colorsList = [
        '#193B8B',
        '#00A0F5',
        '#3AB44A',
        '#227942',
        '#99D9FF',
      ];

      for (let i = 0; i < 5; i += 1) {
        if (this.selectedValuesFormControl.getRawValue()[i]) {
          seriesObject = {
            ...seriesObject,
            data:
              this.chartData[
                String(this.selectedValuesFormControl.getRawValue()[i])
              ] || [],
            name: String(this.selectedValuesFormControl.getRawValue()[i]),
            color: colorsList[i],
            marker: {
              symbol: 'circle',
            },
          };
          dataSeries.push(seriesObject);
        }
      }
    }

    if (dataSeries.length === 0) {
      console.error('No data series found');
      // this.chartOptions.series = [];
      return;
    }

    // TODO Apparently, all of this is necessary to ensure that the data is in the correct format
    // to access is for maxTimestamp down below with firstElementData[firstElementData.length - 1][0] as number
    type DataPoint = [number | string, number | null];

    function isDataPointArray(data: unknown): data is DataPoint[] {
      return (
        Array.isArray(data) &&
        data.every(
          (item) =>
            Array.isArray(item) &&
            item.length === 2 &&
            (typeof item[0] === 'number' || typeof item[0] === 'string') &&
            (typeof item[1] === 'number' || item[1] === null),
        )
      );
    }

    const firstElementData = dataSeries[0].data;
    const lastElementData = dataSeries.findLast(Boolean)?.data;
    if (
      !(
        firstElementData &&
        Array.isArray(firstElementData) &&
        firstElementData.length > 0 &&
        Array.isArray(firstElementData[0]) &&
        Array.isArray(firstElementData.at(-1)) &&
        isDataPointArray(firstElementData)
      ) ||
      !(
        lastElementData &&
        Array.isArray(lastElementData) &&
        lastElementData.length > 0 &&
        Array.isArray(lastElementData[0]) &&
        Array.isArray(lastElementData.at(-1)) &&
        isDataPointArray(lastElementData)
      )
    ) {
      return;
    }

    // TODO style colors using tailwind if possible
    const chartOptions = new ChartOptionsBuilder()
      .withPlotOptions({
        spline: {
          allowPointSelect: false,
          marker: {
            enabled: false,
          },
        },
      })
      .withXAxis({
        type: 'datetime',
        title: {
          text: this.forecastSeries()?.xAxis || 'date',
        },
        plotBands: [
          FORECAST_PLOT_BAND(this.plotBandStartDate, this.seriesMaxDate),
        ],
        plotLines: [FORECAST_PLOT_LINE(this.plotBandStartDate)],
        gridLineWidth: 1,
        gridLineColor: '#E0E0E0',
        gridLineDashStyle: 'Dash',
      })
      .withYAxis({
        title: {
          text: this.translateService.instant(
            'RESULT.INSIGHTS.WIDGETS.TREND_ANALYSIS.Y_AXIS_LABEL',
            {
              target:
                this.targetUnit || this.forecastSeries()?.yAxis || 'value ',
            },
          ),
        },
        plotBands: [
          {
            from: -1000000,
            to: 0,
            color: '#F5F5F5',
          },
        ],
        plotLines: [
          {
            value: 0,
            color: '#E0E0E0',
            dashStyle: 'Dash',
            width: 2,
          },
        ],
        gridLineWidth: 0,
      })
      .withSeries(dataSeries)
      .build();
    this.chartOptions.set(chartOptions);
  }

  async fetchData(): Promise<void> {
    const formControlValue = this.selectedFilterFormControl.getRawValue();
    // TODO handle update logic if no filter value is selected
    if (
      formControlValue &&
      this.selectedValuesFormControl.getRawValue() &&
      this.forecastVersion() !== undefined
    ) {
      this.isLoading = true;
      this.insightsHttpClient
        .getYearToDateTrend(
          this.forecastVersion()?.id ?? '',
          this.selectedValuesFormControl.getRawValue(),
          formControlValue.column,
        )
        .subscribe(async (res) => {
          this.chartData = res;
          await this.setChartOptions();
          this.chartUpdated = true;
          this.isLoading = false;
        });
    } else {
      console.error('No filter value selected');
    }
  }

  setChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
  }

  onFilterChange(): void {
    this.setFilterAndSelectedValues();
    this.fetchData();
  }

  setDefaultFilter() {
    // first select
    if (this.filterColumn) {
      const defaultFilter =
        this.filters().find((filter) => filter.column === this.filterColumn) ??
        this.filters()[0];
      this.selectedFilterFormControl.setValue(defaultFilter);
    } else {
      this.selectedFilterFormControl.setValue(this.filters()[0]);
    }
    // second select
    if (this.filterOptions) {
      this.selectedValuesFormControl.setValue(this.filterOptions);
    } else {
      this.selectedValuesFormControl.setValue(
        this.getDefaultSelectionsForFilterValues(),
      );
    }
    this.possibleFilterValues.set(
      this.selectedFilterFormControl.getRawValue()?.values || [],
    );
  }

  setFilterAndSelectedValues() {
    this.possibleFilterValues.set(
      this.selectedFilterFormControl.getRawValue()?.values || [],
    );
    this.selectedValuesFormControl.setValue(
      this.getDefaultSelectionsForFilterValues(),
    );
  }

  onFilterValueChange(): void {
    this.fetchData();
  }

  toggleSelectAll(selectAllValue: boolean) {
    if (selectAllValue) {
      this.selectedValuesFormControl.setValue(
        this.getDefaultSelectionsForFilterValues(),
      );
    } else {
      this.selectedValuesFormControl.setValue([]);
    }
    this.fetchData();
  }

  // Override the showFullScreen method from the WidgetBaseComponent to handle afterClosed event properly
  showFullScreen(
    componentType: ComponentType<YearToDateTrendWidgetComponent>,
    filterSelection?: DefaultSelectionYearToDateTrendWidget,
  ): MatDialogRef<YearToDateTrendWidgetComponent> | null {
    const dialog = super.showFullScreen(componentType, filterSelection);
    dialog?.afterClosed().subscribe(() => {
      this.filterColumn =
        dialog.componentInstance.selectedFilterFormControl.value.column;
      this.filterOptions =
        dialog.componentInstance.selectedValuesFormControl.value;
      this.setDefaultFilter();
      this.fetchData();
    });
    return null;
  }

  get YearToDateTrendWidgetComponent(): typeof YearToDateTrendWidgetComponent {
    return YearToDateTrendWidgetComponent;
  }
}
